import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Autocomplete,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
// SAP Design System
import { SAP_COLORS, SAP_TYPOGRAPHY, SAP_SPACING, SAP_SHADOWS } from '../theme/sapDesignSystem';
import { SAPIconButton, PageHeader } from '../components';

// API imports
import { fetchClients } from '../api/features/clientData/clientApi';
import { fetchJobDetails } from '../api/features/jobDetail/jobDetailApi';
import {
  fetchFailureMailConfigs,
  fetchFailureMailConfigsCount,
  updateFailureMailConfig,
  createFailureMailConfig,
} from '../api/features/failureMailConfig/failureMailConfigApi';
import { fetchErrorPatternsByClientId } from '../api/features/errorPattern/errorPatternApi';
import { FailureMailConfigData } from '../api/features/failureMailConfig/failureMailConfig.types';
import { useClientCode } from '../api/auth/ClientCodeContext';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

// Types
interface ClientOption {
  label: string;
  value: number;
  code: string;
}

interface JobOption {
  label: string;
  value: string;
}

interface ErrorPatternOption {
  id: number;
  errorCode: string;
}

// Constants
const ROWS_PER_PAGE = 10;

// SAP Styled Components - matching other pages exactly
const SAPPageHeader = styled(Box)(({ theme }) => ({
  marginBottom: SAP_SPACING.xl,
  padding: SAP_SPACING.lg,
  background: `linear-gradient(135deg, ${SAP_COLORS.sapWhite} 0%, ${SAP_COLORS.sapGray1} 100%)`,
  borderRadius: '12px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
}));

const SAPFilterCard = styled(Card)(({ theme }) => ({
  marginBottom: SAP_SPACING.lg,
  borderRadius: '8px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: SAP_SHADOWS.card,
}));

const SAPDataCard = styled(Card)(({ theme }) => ({
  borderRadius: '8px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: SAP_SHADOWS.card,
}));

const SAPTableHeader = styled(TableHead)(({ theme }) => ({
  backgroundColor: SAP_COLORS.sapLightBlue,
  '& .MuiTableCell-head': {
    fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
    color: SAP_COLORS.sapGray6,
    borderBottom: `2px solid ${SAP_COLORS.sapBlue}`,
  },
}));

export default function FailureMailConfig() {
  const { clientCode } = useClientCode();
  const isAdmin = clientCode === 'admin';
  const [selectedClient, setSelectedClient] = useState<ClientOption | null>(null);
  const [selectedJob, setSelectedJob] = useState<JobOption | null>(null);
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);
  const [jobOptions, setJobOptions] = useState<JobOption[]>([]);
  const [failureMailConfigData, setFailureMailConfigData] = useState<FailureMailConfigData[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editConfig, setEditConfig] = useState<FailureMailConfigData | null>(null);
  const [editErrorPatternOptions, setEditErrorPatternOptions] = useState<ErrorPatternOption[]>([]);
  const [editSelectedErrorPattern, setEditSelectedErrorPattern] = useState<ErrorPatternOption | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createClient, setCreateClient] = useState<ClientOption | null>(null);
  const [createJob, setCreateJob] = useState<JobOption | null>(null);
  const [createJobOptions, setCreateJobOptions] = useState<JobOption[]>([]);
  const [createErrorPatternOptions, setCreateErrorPatternOptions] = useState<ErrorPatternOption[]>([]);
  const [createSelectedErrorPattern, setCreateSelectedErrorPattern] = useState<ErrorPatternOption | null>(null);
  const [createLoadingPatterns, setCreateLoadingPatterns] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Load client options on component mount (admin only)
  useEffect(() => {
    if (!isAdmin) return;
    fetchClients({ clientCode, firstResult: -1, maxResult: 0 })
      .then((clients) => {
        setClientOptions(clients.map((c: any) => ({ label: c.name, value: c.id, code: c.code })));
      })
      .catch(() => setClientOptions([]));
  }, [clientCode, isAdmin]);

  // Load job options
  // - Admin: when a client is selected
  // - Client users: all jobs for their clientCode (no explicit client selector)
  useEffect(() => {
    if (isAdmin) {
      if (selectedClient && selectedClient.value) {
        fetchJobDetails({ clientCode, clientId: selectedClient.value, firstResult: -1, maxResult: 0 })
          .then((jobs) =>
            setJobOptions(
              jobs.map((job: any) => ({ label: job[2] || 'Unknown Job', value: job[0], jobName: job[2] }))
            )
          )
          .catch(() => setJobOptions([]));
        setSelectedJob(null);
      } else {
        setJobOptions([]);
        setSelectedJob(null);
      }
      return;
    }

    // Client users: fetch jobs scoped by their clientCode
    fetchJobDetails({ clientCode, firstResult: -1, maxResult: 0 })
      .then((jobs) =>
        setJobOptions(
          jobs.map((job: any) => ({ label: job[2] || 'Unknown Job', value: job[0], jobName: job[2] }))
        )
      )
      .catch(() => setJobOptions([]));
  }, [selectedClient, clientCode, isAdmin]);

  // API Functions - following integration screen pattern
  const loadDropdownOptions = async () => {
    setLoadingOptions(true);
    try {
      // Load clients first
      const clients = await fetchClients({ clientCode: 'admin', firstResult: -1, maxResult: 0 });

      // Process client options
      const processedClients = clients.map((client: any) => ({
        label: client.name || client.code || 'Unknown',
        value: client.id,
        code: client.code,
      }));
      setClientOptions(processedClients);

      // Load all jobs initially
      await loadJobsForClient();
    } catch (error) {
      logger.error('Failed to load dropdown options:', error);
      setClientOptions([]);
      setJobOptions([]);
    } finally {
      setLoadingOptions(false);
    }
  };

  // Load jobs based on selected client
  const loadJobsForClient = async (clientId?: number) => {
    try {
      const params: any = { 
        clientCode: 'admin', 
        firstResult: -1, 
        maxResult: 0
      };
      
      if (clientId) {
        params.clientId = clientId;
      }

      const jobs = await fetchJobDetails(params);

      // Process job options - jobs come as arrays [jobId, interval, jobName, ...]
      const uniqueJobs = jobs.reduce((acc: JobOption[], job: any) => {
        // job[0] = jobId, job[2] = jobName
        const jobId = job[0];
        const jobName = job[2] || 'Unknown';
        
        // Check if this job ID already exists
        const exists = acc.find(item => item.value === jobId);
        if (!exists && jobId) {
          acc.push({
            label: jobName,
            value: jobId, // Use jobId as value for API parameter
          });
        }
        return acc;
      }, []);
      
      setJobOptions(uniqueJobs);
    } catch (error) {
      logger.error('Error loading jobs for client:', error);
      setJobOptions([]);
    }
  };

  // Fetch failure mail config data and count
  const fetchData = async (pageOverride?: number) => {
    setLoading(true);
    setError(null);
    try {
      const currentPage = pageOverride ?? page;
      const firstResult = currentPage * ROWS_PER_PAGE;
      
      const params: any = {
        clientCode: 'admin',
        firstResult,
        maxResult: ROWS_PER_PAGE,
        searched: true,
      };
      
      // Only add parameters if they are selected
      if (selectedClient?.value) params.clientId = selectedClient.value;
      if (selectedJob?.value) params.configName = selectedJob.value;
      
      const [failureMailConfigResponse, failureMailConfigCount] = await Promise.all([
        fetchFailureMailConfigs(params),
        fetchFailureMailConfigsCount({
          clientCode: 'admin',
          clientId: selectedClient?.value,
          configName: selectedJob?.value,
          searched: true,
        }),
      ]);
      
      setFailureMailConfigData(failureMailConfigResponse);
      setRowCount(failureMailConfigCount);
    } catch (err: any) {
      logger.error('Error fetching failure mail config data:', err);
      setError('Failed to load failure mail config data');
      setFailureMailConfigData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    setHasSearched(true);
    fetchData(0);
  };

  const handleReset = () => {
    setSelectedClient(null);
    setSelectedJob(null);
    setPage(0);
    setHasSearched(false);
    setError(null);
    setFailureMailConfigData([]);
    setRowCount(0);
  };

  const handleCreate = () => {
    setCreateDialogOpen(true);
    setCreateClient(null);
    setCreateJob(null);
    setCreateJobOptions([]);
    setCreateErrorPatternOptions([]);
    setCreateSelectedErrorPattern(null);
    setCreateError(null);
  };

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
    fetchData(newPage);
  };

  const handleClientChange = (event: any, newClient: ClientOption | null) => {
    setSelectedClient(newClient);
  };

  const handleJobChange = (event: any, newJob: JobOption | null) => {
    setSelectedJob(newJob);
  };

  const handleEditClick = async (row: FailureMailConfigData) => {
    setEditDialogOpen(true);
    setEditConfig(row);
    setEditSelectedErrorPattern(null);
    setEditError(null);
    setEditLoading(true);
    setEditErrorPatternOptions([]);
    try {
      const patterns = await fetchErrorPatternsByClientId({
        clientCode: 'admin',
        clientId: row.clientId,
      });
      setEditErrorPatternOptions(patterns);
      const match = patterns.find(
        (p) => p.id === row.notInErrorPatternId || p.errorCode === row.errorName
      );
      setEditSelectedErrorPattern(match ?? null);
    } catch (err: any) {
      setEditError(err?.message || 'Failed to load error patterns');
      setEditErrorPatternOptions([]);
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditClose = () => {
    if (!editSaving && !editLoading) {
      setEditDialogOpen(false);
      setEditConfig(null);
      setEditSelectedErrorPattern(null);
      setEditError(null);
    }
  };

  const handleEditSave = async () => {
    if (!editConfig || !editSelectedErrorPattern) return;
    setEditSaving(true);
    setEditError(null);
    try {
      const payload = {
        createdBy: null,
        creationDate: null,
        lastUpdateDate: Date.now(),
        lastUpdatedBy: null,
        clientId: editConfig.clientId,
        id: editConfig.id,
        jobDetailId: editConfig.jobId,
        notInErrorPatternId: editSelectedErrorPattern.id,
      };
      const res = await updateFailureMailConfig({
        clientCode: 'admin',
        payload,
      });
      if (res?.success !== false) {
        toast.success('Save updated successfully');
        setEditDialogOpen(false);
        setEditConfig(null);
        setEditSelectedErrorPattern(null);
        fetchData(page);
      } else {
        toast.error(res?.messages?.join(', ') || 'Failed to save');
      }
    } catch (err: any) {
      const msg = err?.message || 'Failed to save';
      setEditError(msg);
      toast.error(msg);
    } finally {
      setEditSaving(false);
    }
  };

  // Create: load jobs when client selected
  useEffect(() => {
    if (!createDialogOpen || !createClient?.value) {
      setCreateJobOptions([]);
      setCreateJob(null);
      setCreateErrorPatternOptions([]);
      setCreateSelectedErrorPattern(null);
      return;
    }
    fetchJobDetails({
      clientCode: 'admin',
      clientId: createClient.value,
      firstResult: -1,
      maxResult: 0,
    })
      .then((jobs) => {
        const uniqueJobs = jobs.reduce((acc: JobOption[], job: any) => {
          const jobId = job[0];
          const jobName = job[2] || 'Unknown';
          const val = String(jobId);
          if (!acc.find((item) => item.value === val) && jobId) {
            acc.push({ label: jobName, value: val });
          }
          return acc;
        }, []);
        setCreateJobOptions(uniqueJobs);
        setCreateJob(null);
      })
      .catch(() => setCreateJobOptions([]));

    setCreateLoadingPatterns(true);
    fetchErrorPatternsByClientId({
      clientCode: 'admin',
      clientId: createClient.value,
    })
      .then((patterns) => {
        setCreateErrorPatternOptions(patterns);
        setCreateSelectedErrorPattern(null);
      })
      .catch(() => setCreateErrorPatternOptions([]))
      .finally(() => setCreateLoadingPatterns(false));
  }, [createDialogOpen, createClient?.value]);

  const handleCreateClose = () => {
    if (!createSaving) {
      setCreateDialogOpen(false);
      setCreateClient(null);
      setCreateJob(null);
      setCreateSelectedErrorPattern(null);
      setCreateError(null);
    }
  };

  const handleCreateSave = async () => {
    if (!createClient?.value || !createJob?.value || !createSelectedErrorPattern) return;
    setCreateSaving(true);
    setCreateError(null);
    try {
      await createFailureMailConfig({
        clientCode: 'admin',
        payload: {
          clientId: createClient.value,
          jobDetailId: Number(createJob.value),
          notInErrorPatternId: createSelectedErrorPattern.id,
        },
      });
      toast.success('Failure mail configuration created successfully');
      setCreateDialogOpen(false);
      setCreateClient(null);
      setCreateJob(null);
      setCreateSelectedErrorPattern(null);
      fetchData(page);
    } catch (err: any) {
      const msg = err?.message || 'Failed to create';
      setCreateError(msg);
      toast.error(msg);
    } finally {
      setCreateSaving(false);
    }
  };

  return (
    <Box sx={{ p: SAP_SPACING.lg, bgcolor: SAP_COLORS.sapGray1, minHeight: '100vh' }}>
      {/* Page Header */}
      <PageHeader 
        title="Failure Mail Configuration"
        description="Search and manage failure mail configurations for clients and jobs"
      />

      {/* SAP Fiori Filter Card - exactly like other pages */}
      <SAPFilterCard>
        <CardContent sx={{ p: SAP_SPACING.lg }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr auto' }, 
            gap: SAP_SPACING.md,
            alignItems: 'end'
          }}>
            {/* Client Name Autocomplete */}
            <Box>
              <Autocomplete
                options={clientOptions}
                getOptionLabel={(option) => option.label}
                value={selectedClient}
                onChange={handleClientChange}
                renderInput={(params) => (
                  <TextField {...params} label="Client Name" />
                )}
              />
            </Box>
            
            {/* Job Name Autocomplete */}
            <Box>
              <Autocomplete
                options={jobOptions}
                getOptionLabel={(option) => option.label}
                value={selectedJob}
                onChange={handleJobChange}
                disabled={!selectedClient}
                renderInput={(params) => (
                  <TextField {...params} label="Job Name" />
                )}
              />
            </Box>
            
            {/* Action Buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: { xs: 'center', sm: 'flex-end' }
            }}>
              <SAPIconButton
                icon="search"
                variant="primary"
                onClick={handleSearch}
                disabled={loading || loadingOptions}
                aria-label="Search"
              />
              <SAPIconButton
                icon="reset"
                variant="secondary"
                onClick={handleReset}
                disabled={loading || loadingOptions}
                aria-label="Reset"
              />
              <SAPIconButton
                icon="add"
                variant="success"
                onClick={() => handleCreate()}
                disabled={loading || loadingOptions}
                aria-label="Create"
              />
            </Box>
          </Box>
        </CardContent>
      </SAPFilterCard>

      {/* SAP Fiori Data Card - exactly like other pages */}
      <SAPDataCard>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
              <CircularProgress sx={{ color: SAP_COLORS.sapBlue }} size={48} />
            </Box>
          ) : error ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
              <Alert severity="error" sx={{ 
                borderRadius: '8px',
                backgroundColor: SAP_COLORS.sapRed + '10',
                color: SAP_COLORS.sapRed,
                border: `1px solid ${SAP_COLORS.sapRed}30`,
              }}>
                {error}
              </Alert>
            </Box>
          ) : !hasSearched && failureMailConfigData.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
              <Typography variant="body1" sx={{ color: SAP_COLORS.sapGray5 }}>
                Please use the search filters above to view failure mail configuration data.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table sx={{ minWidth: 650 }}>
                  {/* SAP Fiori Table Header */}
                  <SAPTableHeader>
                    <TableRow>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray6, 
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
                        width: '80px'
                      }}>
                        S.No.
                      </TableCell>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray6, 
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold 
                      }}>
                        Client Name
                      </TableCell>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray6, 
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold 
                      }}>
                        Job Name
                      </TableCell>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray6, 
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold 
                      }}>
                        Error Name
                      </TableCell>
                    </TableRow>
                  </SAPTableHeader>
                  
                  {/* Table Body */}
                  <TableBody>
                    {failureMailConfigData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ 
                          color: SAP_COLORS.sapGray5,
                          py: SAP_SPACING.xl,
                          fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                        }}>
                          No failure mail configuration data found. Try searching with different criteria.
                        </TableCell>
                      </TableRow>
                    ) : (
                      failureMailConfigData.map((row, idx) => (
                        <TableRow
                          key={`failuremailconfig-${page * ROWS_PER_PAGE + idx + 1}-${row.clientName || idx}-${row.jobName || idx}`}
                          onClick={() => handleEditClick(row)}
                          sx={{
                            bgcolor: idx % 2 === 0 ? SAP_COLORS.sapWhite : SAP_COLORS.sapGray1,
                            cursor: 'pointer',
                            '&:hover': { 
                              bgcolor: SAP_COLORS.hover,
                            },
                            transition: 'background-color 0.2s ease',
                          }}
                        >
                          <TableCell sx={{ 
                            color: SAP_COLORS.sapGray6, 
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium 
                          }}>
                            {page * ROWS_PER_PAGE + idx + 1}
                          </TableCell>
                          <TableCell sx={{ 
                            color: SAP_COLORS.sapGray7,
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                          }}>
                            {row.clientName || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ 
                            color: SAP_COLORS.sapGray7,
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                          }}>
                            {row.jobName || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ color: SAP_COLORS.sapGray6 }}>
                            {row.errorName || 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination */}
              {failureMailConfigData.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {/* Pagination Info */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Showing {(page * ROWS_PER_PAGE) + 1}-{Math.min((page * ROWS_PER_PAGE) + failureMailConfigData.length, rowCount)} of {rowCount} mail configurations
                    </Typography>
                  </Box>
                  
                  {/* Pagination Controls */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Rows per page</InputLabel>
                      <Select
                        value={ROWS_PER_PAGE}
                        onChange={(event: SelectChangeEvent<number>) => {
                          // Note: ROWS_PER_PAGE is constant, so this is just for UI consistency
                        }}
                        label="Rows per page"
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <Pagination
                      count={Math.ceil(rowCount / ROWS_PER_PAGE)}
                      page={page + 1}
                      onChange={(event: React.ChangeEvent<unknown>, newPage: number) => {
                        handleChangePage(event, newPage - 1);
                      }}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </SAPDataCard>

      {/* Edit dialog: GET by id, show response, allow only Error Name to be modified, PUT on save */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${SAP_COLORS.sapGray2}`,
            boxShadow: SAP_SHADOWS.card,
          },
        }}
      >
        <DialogTitle sx={{ color: SAP_COLORS.sapGray7, fontWeight: SAP_TYPOGRAPHY.fontWeight.bold, borderBottom: `1px solid ${SAP_COLORS.sapGray2}`, pb: 2 }}>
          Edit Failure Mail Configuration
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {editLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress sx={{ color: SAP_COLORS.sapBlue }} size={40} />
            </Box>
          ) : editConfig ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Client Name"
                value={editConfig.clientName ?? 'N/A'}
                fullWidth
                size="small"
                disabled
                sx={{ '& .MuiInputBase-input': { color: SAP_COLORS.sapGray6 } }}
              />
              <TextField
                label="Job Name"
                value={editConfig.jobName ?? 'N/A'}
                fullWidth
                size="small"
                disabled
                sx={{ '& .MuiInputBase-input': { color: SAP_COLORS.sapGray6 } }}
              />
              <Autocomplete<ErrorPatternOption>
                options={editErrorPatternOptions}
                getOptionLabel={(opt) => opt.errorCode}
                value={editSelectedErrorPattern}
                onChange={(_, v) => setEditSelectedErrorPattern(v)}
                fullWidth
                size="small"
                renderInput={(params) => (
                  <TextField {...params} label="Error Name" placeholder="Select error pattern" />
                )}
                disabled={editSaving || editErrorPatternOptions.length === 0}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: SAP_COLORS.sapWhite } }}
              />
              {editError && (
                <Alert severity="error" sx={{ mt: 1 }} onClose={() => setEditError(null)}>
                  {editError}
                </Alert>
              )}
            </Box>
          ) : null}
        </DialogContent>
        {editConfig && !editLoading && (
          <DialogActions sx={{ p: 2, borderTop: `1px solid ${SAP_COLORS.sapGray2}`, gap: 1 }}>
            <Button
              onClick={handleEditClose}
              disabled={editSaving}
              sx={{ color: SAP_COLORS.sapGray6, textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleEditSave}
              disabled={editSaving || !editSelectedErrorPattern}
              sx={{
                backgroundColor: SAP_COLORS.sapBlue,
                '&:hover': { backgroundColor: SAP_COLORS.sapBlueDark },
                textTransform: 'none',
              }}
            >
              {editSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Create dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={handleCreateClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${SAP_COLORS.sapGray2}`,
            boxShadow: SAP_SHADOWS.card,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: SAP_COLORS.sapGray7,
            fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
            pb: 2,
          }}
        >
          Create Failure Mail Configuration
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Autocomplete<ClientOption>
                options={clientOptions}
                getOptionLabel={(o) => o.label}
                value={createClient}
                onChange={(_, v) => setCreateClient(v)}
                renderInput={(params) => <TextField {...params} label="Client Name" />}
                disabled={createSaving}
              />
              <Autocomplete<JobOption>
                options={createJobOptions}
                getOptionLabel={(o) => o.label}
                value={createJob}
                onChange={(_, v) => setCreateJob(v)}
                renderInput={(params) => <TextField {...params} label="Job Name" />}
                disabled={createSaving || !createClient}
              />
              <Autocomplete<ErrorPatternOption>
                options={createErrorPatternOptions}
                getOptionLabel={(o) => o.errorCode}
                value={createSelectedErrorPattern}
                onChange={(_, v) => setCreateSelectedErrorPattern(v)}
                loading={createLoadingPatterns}
                renderInput={(params) => (
                  <TextField {...params} label="Error Name" placeholder="Select error pattern" />
                )}
                disabled={createSaving || !createClient || createErrorPatternOptions.length === 0}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: SAP_COLORS.sapWhite } }}
              />
            </Box>
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 1,
                bgcolor: SAP_COLORS.sapGray1,
                border: `1px solid ${SAP_COLORS.sapGray2}`,
              }}
            >
              {!createClient ? (
                <Typography variant="body2" sx={{ color: SAP_COLORS.sapGray6 }}>
                  Select client to add the error name.
                </Typography>
              ) : createLoadingPatterns ? (
                <Typography variant="body2" sx={{ color: SAP_COLORS.sapGray6 }}>
                  Loading error patterns...
                </Typography>
              ) : createErrorPatternOptions.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ color: '#B91C1C' }}
                >
                  {createClient?.label ?? 'This client'} has no Error Patterns. Kindly add the error pattern in Error
                  Pattern under this client and then config failure mail alert.
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ color: SAP_COLORS.sapGray6 }}>
                  Select an error pattern from the dropdown above.
                </Typography>
              )}
            </Box>
            {createError && (
              <Alert severity="error" sx={{ mt: 1 }} onClose={() => setCreateError(null)}>
                {createError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${SAP_COLORS.sapGray2}`, gap: 1 }}>
          <Button
            onClick={handleCreateClose}
            disabled={createSaving}
            sx={{ color: SAP_COLORS.sapGray6, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateSave}
            disabled={createSaving || !createClient || !createJob || !createSelectedErrorPattern}
            sx={{
              backgroundColor: SAP_COLORS.sapBlue,
              '&:hover': { backgroundColor: SAP_COLORS.sapBlueDark },
              textTransform: 'none',
            }}
          >
            {createSaving ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}